import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Crown, 
  Plus, 
  RefreshCw, 
  AlertTriangle,
  ArrowUpRight,
  Zap,
  Package
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import AddonPurchaseModal from './AddonPurchaseModal';

/**
 * TierLimitModal Component
 * 
 * Shows when vendor tries to publish a listing but exceeds tier limits.
 * Provides 3 options per spec:
 * 1. Upgrade Plan (Recommended)
 * 2. Buy Add-on (Tactical Fix) - Now with Razorpay payment
 * 3. Switch Store Type (Free Fallback)
 */
const TierLimitModal = ({
  open,
  onOpenChange,
  errorType,
  message,
  options = {},
  currentTypes = [],
  newType,
  typesLimit,
  currentCount,
  limit,
  onSwitchComplete,
  onAddonPurchased
}) => {
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [switching, setSwitching] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showAddonPurchase, setShowAddonPurchase] = useState(false);

  const handleUpgrade = () => {
    toast({
      title: 'Redirecting to Plans',
      description: 'Choose a plan that fits your business needs.',
    });
    onOpenChange(false);
    navigate('/vendor/subscription-plans');
  };

  const handleBuyAddon = () => {
    // Show the addon purchase modal with Razorpay
    setShowAddonPurchase(true);
  };

  const handleAddonPurchaseComplete = (result) => {
    setShowAddonPurchase(false);
    onOpenChange(false);
    toast({
      title: 'Add-on Activated!',
      description: 'You can now publish your listing.',
    });
    onAddonPurchased?.(result);
  };

  const handleSwitchType = async () => {
    if (!confirmSwitch) {
      setConfirmSwitch(true);
      return;
    }

    if (confirmText.toUpperCase() !== 'SWITCH') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type "SWITCH" to confirm',
        variant: 'destructive'
      });
      return;
    }

    setSwitching(true);
    try {
      const result = await apiCall('/api/listings/switch-store-type', {
        method: 'POST',
        body: JSON.stringify({ new_type: newType })
      });

      if (result.success) {
        toast({
          title: 'Store Type Switched',
          description: result.message,
        });

        // Show undo notification
        if (result.undo_id && onSwitchComplete) {
          onSwitchComplete({
            undoId: result.undo_id,
            expiresAt: result.undo_expires_at,
            unpublishedCount: result.unpublished_count,
            newType: newType
          });
        }

        onOpenChange(false);
      } else {
        throw new Error(result.message || 'Failed to switch store type');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSwitching(false);
    }
  };

  const resetConfirm = () => {
    setConfirmSwitch(false);
    setConfirmText('');
  };

  // Count limit exceeded
  if (errorType === 'count_limit') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Listing Limit Reached
            </DialogTitle>
            <DialogDescription>
              {message || `You have used ${currentCount}/${limit} active listings.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-amber-800">Current Usage</span>
                <Badge variant="outline" className="text-amber-700 border-amber-300">
                  {currentCount} / {limit} Listings
                </Badge>
              </div>
              <p className="text-sm text-amber-700">
                Delete existing listings or upgrade your plan to publish more.
              </p>
            </div>

            {/* Option 1: Upgrade (Recommended) */}
            <Button 
              onClick={handleUpgrade}
              className="w-full justify-between bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4" />
                <span>Upgrade Plan</span>
              </div>
              <div className="flex items-center gap-1">
                <Badge className="bg-white/20 text-white text-xs">Recommended</Badge>
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Maybe Later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Type limit exceeded - show 3 options
  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) resetConfirm(); onOpenChange(val); }}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <Package className="h-5 w-5" />
            New Listing Type Detected
          </DialogTitle>
          <DialogDescription>
            {message || `Your plan allows ${typesLimit} listing type(s).`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current State Info */}
          <div className="p-4 bg-slate-50 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-slate-600">Currently Active:</span>
              {currentTypes.map(type => (
                <Badge key={type} variant="secondary" className="capitalize">
                  {type}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Trying to Add:</span>
              <Badge className="bg-blue-100 text-blue-700 capitalize">
                {newType}
              </Badge>
            </div>
          </div>

          {!confirmSwitch ? (
            <>
              {/* Option A: Upgrade Plan (Recommended) */}
              <div 
                className="p-4 border-2 border-blue-200 bg-blue-50/50 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                onClick={handleUpgrade}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Crown className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="font-semibold text-blue-900">
                      {options?.upgrade?.label || 'Upgrade Plan'}
                    </span>
                  </div>
                  <Badge className="bg-blue-600">⭐ Recommended</Badge>
                </div>
                <p className="text-sm text-blue-700 ml-10">
                  {options?.upgrade?.description || 'Get more listing types AND increase your listing limit.'}
                </p>
              </div>

              {/* Option B: Buy Add-on */}
              <div 
                className="p-4 border border-slate-200 rounded-lg cursor-pointer hover:border-slate-400 transition-colors"
                onClick={handleBuyAddon}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Zap className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="font-semibold text-slate-900">
                      {options?.addon?.label || 'Buy Add-on'}
                    </span>
                  </div>
                  {options?.addon?.price && (
                    <Badge variant="outline">₹{options.addon.price}/mo</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600 ml-10">
                  {options?.addon?.description || 'Keep your current plan but add one more listing type.'}
                </p>
              </div>

              {/* Option C: Switch Store Type (Free) */}
              <div 
                className="p-4 border border-orange-200 bg-orange-50/30 rounded-lg cursor-pointer hover:border-orange-400 transition-colors"
                onClick={handleSwitchType}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <RefreshCw className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="font-semibold text-slate-900">
                      {options?.switch?.label || 'Switch Store Type'}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-green-600 border-green-300">Free</Badge>
                </div>
                <p className="text-sm text-slate-600 ml-10">
                  {options?.switch?.description || `Unpublish all current listings and switch to ${newType}s.`}
                </p>
                <div className="mt-2 ml-10 flex items-center gap-1 text-xs text-orange-600">
                  <AlertTriangle className="h-3 w-3" />
                  {options?.switch?.warning || 'This will remove listings from search results.'}
                </div>
              </div>
            </>
          ) : (
            /* Confirmation for Switch */
            <div className="p-4 border-2 border-orange-300 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <span className="font-semibold text-orange-900">Confirm Store Type Switch</span>
              </div>
              <p className="text-sm text-orange-800 mb-3">
                ⚠️ <strong>Warning: Loss of Visibility.</strong> Switching store types will immediately 
                unpublish all {currentTypes.length > 0 ? currentTypes.map(t => `${t}s`).join(' and ') : 'active listings'}. 
                They will disappear from search results, SEO indexes, and customer views.
              </p>
              <div className="mb-3">
                <label className="text-sm font-medium text-orange-900">
                  Type "SWITCH" to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-orange-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Type SWITCH"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={resetConfirm}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSwitchType}
                  disabled={switching || confirmText.toUpperCase() !== 'SWITCH'}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {switching ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Switching...
                    </>
                  ) : (
                    'Confirm Switch'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {!confirmSwitch && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
      
      {/* Addon Purchase Modal with Razorpay */}
      <AddonPurchaseModal
        open={showAddonPurchase}
        onOpenChange={setShowAddonPurchase}
        addon={{
          key: options?.addon?.addon_key || 'extra_listing_type',
          name: options?.addon?.addon_name || 'Additional Listing Type',
          description: options?.addon?.description || 'Add one more listing type to your store',
          price: options?.addon?.price || 500,
          features: [
            'Publish multiple listing types',
            'Switch between types anytime',
            'No limit on draft creations'
          ]
        }}
        onPurchaseComplete={handleAddonPurchaseComplete}
      />
    </Dialog>
  );
};

export default TierLimitModal;
